import { Food, IngredientFood } from '../../../../core/db_food'
import { decryptUtf8WithPassword, gunzipBytesToUtf8, isEncryptedFoodsFileJson } from './foodsExportCrypto'


/** Своё расширение: проще фильтровать в диалоге импорта (`accept`). Внутри — JSON или JSON-конверт шифрования. */
export const FOODS_EXPORT_EXT = '.diet' as const
export const FOODS_EXPORT_GZ_EXT = '.diet.gz' as const

export async function importFoodsFromDietFile(file: File, importPassword?: string): Promise<Food[]> {
    const utf8 = await readDietFileToUtf8(file)
    let jsonStr = utf8
    if (isEncryptedFoodsFileJson(utf8)) {
        if (importPassword == null || importPassword.trim() === '') {
            importPassword = 'default-pass'
        }
        jsonStr = await decryptUtf8WithPassword(utf8.trim(), importPassword.trim())
    }
    const foods = parseFoodsJsonArray(jsonStr)
    return foods
}

async function readDietFileToUtf8(file: File): Promise<string> {
    const name = file.name.toLowerCase()
    const raw = new Uint8Array(await file.arrayBuffer())
    if (name.endsWith(FOODS_EXPORT_GZ_EXT)) {
        return gunzipBytesToUtf8(raw)
    }
    if (name.endsWith(FOODS_EXPORT_EXT)) {
        return new TextDecoder('utf-8').decode(raw)
    }
    throw new Error(`Ожидался файл ${FOODS_EXPORT_GZ_EXT} или .csv`)
}

function parseFoodsJsonArray(jsonStr: string): Food[] {
    let parsed: unknown
    try {
        parsed = JSON.parse(jsonStr) as unknown
    } catch {
        throw new Error('В файле не JSON или повреждённый текст после расшифровки')
    }
    if (!Array.isArray(parsed)) {
        throw new Error('Ожидался JSON-массив продуктов')
    }
    for (let i = 0; i < parsed.length; i++) {
        const item = parsed[i]
        if (item === null || typeof item !== 'object') {
            throw new Error(`Элемент ${i}: ожидался объект продукта`)
        }
        const o = item as Record<string, unknown>
        if (typeof o.dt !== 'number' || typeof o.name !== 'string') {
            throw new Error(`Элемент ${i}: у продукта нужны поля dt (число) и name (строка)`)
        }
    }
    return parsed as Food[]
}

export async function readCsvFileToUtf8(code: 'windows-1251' | 'utf-8', file: File): Promise<[string[], string]> {
    const raw = new Uint8Array(await file.arrayBuffer())
    const text = new TextDecoder(code).decode(raw)
    const sepNewLine = detectNewLine(text)
    const lines = text.split(sepNewLine)
    const separator = detectColumnSeparator(lines)
    console.log('separator', separator)
    // windows decode
    return [lines, separator]
}

function detectNewLine(text: string): string {
    if (text.includes('\r\n')) {
        return '\r\n'
    }
    return '\n'
}

const COLUMN_SEPARATORS = ['\t', ';', ','] as const

/** Приоритет при равном числе колонок (типичный Excel в RU/ EU — `;`, таблицы — `\t`). */
const SEPARATOR_TIE_ORDER: Record<(typeof COLUMN_SEPARATORS)[number], number> = {
    '\t': 0,
    ';': 1,
    ',': 2,
}

function detectColumnSeparator(lines: string[]): string {
    const maxSample = 50
    let sample = lines.slice(0, maxSample).map(line => line.replace(/\r$/, ''))
    if (sample.length >= maxSample) {
        sample = sample.slice(0, -1)
    }
    const rows = sample.map(line => line.trim()).filter(line => line.length > 0)
    if (rows.length === 0) {
        return ';'
    }

    type Candidate = { sep: (typeof COLUMN_SEPARATORS)[number]; cols: number }
    const candidates: Candidate[] = []

    for (const sep of COLUMN_SEPARATORS) {
        const colCounts = rows.map(line => splitCsvLine(sep, line).length)
        const minCols = Math.min(...colCounts)
        const maxCols = Math.max(...colCounts)
        if (minCols >= 2 && minCols === maxCols) {
            candidates.push({ sep, cols: minCols })
        }
    }

    if (candidates.length === 0) {
        return ';'
    }

    const bestCols = Math.max(...candidates.map(c => c.cols))
    const best = candidates.filter(c => c.cols === bestCols)
    best.sort((a, b) => SEPARATOR_TIE_ORDER[a.sep] - SEPARATOR_TIE_ORDER[b.sep])
    return best[0].sep
}


/**
 * Одна строка CSV: разделитель не действует внутри поля в двойных кавычках; `""` внутри кавычек — литеральная `"`.
 * Строка уже без перевода строки (как после `split` по `\n`); переносы внутри кавычек здесь не склеиваются.
 */
export function splitCsvLine(separator: string, line: string): string[] {
    if (separator === '') {
        return [line]
    }
    const fields: string[] = []
    let field = ''
    let inQuotes = false
    let i = 0
    while (i < line.length) {
        const c = line[i]
        if (inQuotes) {
            if (c === '"') {
                if (line[i + 1] === '"') {
                    field += '"'
                    i += 2
                } else {
                    inQuotes = false
                    i += 1
                }
            } else {
                field += c
                i += 1
            }
        } else if (c === '"') {
            inQuotes = true
            i += 1
        } else if (line.startsWith(separator, i)) {
            fields.push(field)
            field = ''
            i += separator.length
        } else {
            field += c
            i += 1
        }
    }
    fields.push(field)
    return fields
}

const INVALID_NUMBER_MSG = 'Некорректное число'

/** Десятичное число из строки импорта: без NaN, при сомнительном формате — исключение. */
export function parseNumber(txt: string): number {
    const compact = txt.trim().replace(/\s+/g, '')
    if (compact === '') {
        throw new Error(INVALID_NUMBER_MSG)
    }
    if (compact.includes(',') && compact.includes('.')) {
        throw new Error(INVALID_NUMBER_MSG)
    }
    const commaCount = (compact.match(/,/g) ?? []).length
    const dotCount = (compact.match(/\./g) ?? []).length
    if (commaCount > 1 || dotCount > 1) {
        throw new Error(INVALID_NUMBER_MSG)
    }
    const normalized = compact.replace(',', '.')
    if (!/^[-+]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(normalized)) {
        throw new Error(INVALID_NUMBER_MSG)
    }
    const n = Number(normalized)
    if (!Number.isFinite(n)) {
        throw new Error(INVALID_NUMBER_MSG)
    }
    return n
}